import React, {
    lazy,
    useContext, useEffect, useMemo, useRef, useState,
} from "react";
import hasContainer from "@root/helpers/has-container";
import ActionSheetTabs from "@_components/action-sheet-tabs";
import AccordionMenu from "@_components/accordion-menu";
import ProductGrid from "@_components/product-grid";
import { CategoryContext } from "@root/context/category";
import mapNestedObjects from "@helpers/mapNestedObject";
import navigate from "@helpers/link-navigate";
import addParamsToUrl from "@helpers/add-params-to-url";
import CategoryGrid from "@root/_components/category-grid";
import { trackSelectItem, trackViewCollection } from "@root/analytics";

const AccordionProductLayoutModule = ({ data }) => {
    if (!data) return null;

    const { baseCategory, currentCategory, currentCategoryProducts, navigateToCategory, loading, pageData } = useContext(CategoryContext) || {};

    if (!baseCategory || !currentCategory) return null;

    const actionSheetRef = useRef(null);
    const titleRef = useRef(null);

    /* Categories */
    const categories = useMemo(() => mapNestedObjects(baseCategory.subcategories, "subcategories", "subItems", (category, depth) => ({
        id: category.id,
        title: category.name,
        ...(depth > 1 && { subtitle: `${category.product_count} Products` }),
        image: depth <= 1 ? category.icon_url : category.image_url,
        url: pageData?.handle?.current ? `${pageData?.handle?.current}/${category.slug}` : category.slug,
        subItems: category.subItems,
        slug: category.slug,
    })), [baseCategory]);

    /* Products */
    const products = useMemo(() => currentCategoryProducts.map((product) => ({
        ...product,
        id: product.id,
        category: product.category,
        image: (product.listing_image ?? product.image_url) ? addParamsToUrl(product.listing_image ?? product.image_url, { width: 401 }) : '',
        alt: product.title,
        url: `/products/${product.handle}`,
        title: product.title,
        price: product.price,
    })), [currentCategoryProducts]);

    /* Active Items */
    const [activeItems, setActiveItems] = useState([]);

    const changeCategory = (e, category, _categories) => {
        e.preventDefault();
        setActiveItems(_categories);
        navigateToCategory(category);

        if (category.subItems && category.subItems.length === 0) {
            actionSheetRef?.current?.closePanel();
        }

        const targetElement = document.getElementById("patio-scroll-top");
        if (targetElement) {
            window.scrollTo({
                top: targetElement.offsetTop - 20,
                behavior: "smooth",
            });
        }
    };

    /* Current sub categories for mobile view */
    const currentSubCategories = categories?.filter((category) => category?.slug === currentCategory?.slug)?.[0]?.subItems ?? [];

    useEffect(() => {
        trackViewCollection(currentCategoryProducts ?? [], currentCategory.name, currentCategory.slug);

        if (currentCategory.paths && currentCategory.paths.length > 0) {
            const pathItems = currentCategory.paths.map((path) => path.id);
            pathItems.shift();

            if (pathItems.join(",") !== activeItems.join(",")) {
                setActiveItems(pathItems);
            }
        }
    }, [currentCategory]);

    /* Visible Products */
    const productRefs = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const visibleProdIds = [];
                if (currentCategory.paths.length > 2) {
                    return;
                }
                entries.forEach((entry) => {
                    const visibleProductCategoryId = parseInt(entry.target.getAttribute("data-category-id"), 10);
                    if (entry.isIntersecting) {
                        visibleProdIds.push(visibleProductCategoryId);
                    }
                    // } else {
                    //     setActiveItems((prevItems) => prevItems.filter((item) => item !== visibleProductCategoryId));
                    // }
                });
                if (visibleProdIds.length) {
                    const middleProductId = visibleProdIds[1];
                    const middleProdCategoryCount = visibleProdIds.filter((_id) => _id === middleProductId).length;

                    if (middleProdCategoryCount > 1) {
                        setActiveItems([currentCategory.id, middleProductId]);
                    } else {
                        setActiveItems([currentCategory.id, ...visibleProdIds]);
                    }
                }
            },
            { threshold: 0.5, rootMargin: "0px 0px -50% 0px" },
        );

        productRefs.current.forEach((product) => {
            if (product) observer.observe(product);
        });

        return () => observer.disconnect();
    }, [currentCategory]);

    const handleItemClick = (e, item) => {
        trackSelectItem(item, currentCategory.name, currentCategory.slug);
    };

    return (
        <div className={`patio-category ${loading ? "patio-category--loading" : ""} ${hasContainer(data?.moduleSettings?.hasContainer) !== "container" ? "p-0" : "container"}  ${currentSubCategories.length > 0 ? "patio-category--has-subCategories" : ""}`}>
            <div className="patio-category__title" ref={titleRef}>
                <h1>{currentCategory.name}</h1>
                <p className="patio-category__product-amount">
                    <span>{products.length}</span>
                    {" "}
                    Products Available
                </p>
            </div>
            <div className="patio-category__wrapper">
                <div className="patio-category__accordion-wrapper">
                    <div className="patio-category__accordion-wrapper--desktop">
                        <AccordionMenu items={categories} onChange={changeCategory} activeItems={activeItems} />
                    </div>
                    <div className="patio-category__accordion-wrapper--mobile">
                        <ActionSheetTabs ref={actionSheetRef}>
                            <div title="Choose your category" icon="filter">
                                <AccordionMenu items={categories} onChange={changeCategory} activeItems={activeItems} />
                            </div>
                        </ActionSheetTabs>
                    </div>
                </div>
                {currentSubCategories.length > 0 && (
                    <div className="patio-category__subcategories--mobile">
                        <CategoryGrid items={currentSubCategories} />
                    </div>
                )}
                <div className="patio-category__product-grid-wrapper">
                    <ProductGrid items={products} productRefs={productRefs} onClick={handleItemClick} />
                </div>
            </div>
        </div>
    );
};

export default AccordionProductLayoutModule;
